/**
 * Cost Dashboard — LLM spend overview.
 *
 * Single-page, density-first (per design book §2). No external chart
 * library — small SVG sparkline + CSS bars are enough at this density
 * and keep the bundle lean. Recharts / Highcharts come in later when
 * we need interactive zoom.
 *
 * Wires four data shapes from `/v1/observability/llm-cost`:
 *   - totals (cost / tokens / latency / cache-hit)
 *   - daily timeseries (sparkline)
 *   - breakdowns by provider / agent / task_type (bars)
 */

import { useCallback, useMemo, useState } from "react";
import { Activity, BarChart3, Coins, Cpu, RefreshCw, Zap } from "lucide-react";
import { Button, Card, CardHeader } from "@vseh/ui";

import {
  observabilityApi,
  type CostReport,
  type CostDailyPoint,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock, LoadingBlock } from "../shared/components/States";


// Range labels are keys, resolved per-render through useT so the
// language switch updates them too.
const WINDOW_OPTIONS = [
  { value: 7,   labelKey: "cost.range.7"   },
  { value: 30,  labelKey: "cost.range.30"  },
  { value: 90,  labelKey: "cost.range.90"  },
  { value: 365, labelKey: "cost.range.365" },
] as const;


export function CostDashboardPage(): JSX.Element {
  // Aliased to `tt` because the by_task_type breakdown rows
  // already use `t` as the iteration variable below.
  const tt = useT();
  const [windowDays, setWindowDays] = useState(30);

  const fetcher = useCallback(
    (signal: AbortSignal) => observabilityApi.llmCost(windowDays, { signal }),
    [windowDays],
  );
  const { data, error, loading, refetch } = useFetch<CostReport>(
    fetcher, [windowDays],
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={tt("cost.title")}
          description={tt("cost.subtitle")}
          actions={
            <div className="flex items-center gap-2">
              <WindowPicker value={windowDays} onChange={setWindowDays} />
              <Button variant="ghost" size="xs"
                      leftIcon={<RefreshCw className="size-3.5" />}
                      onClick={refetch}>
                {tt("common.refresh")}
              </Button>
            </div>
          }
        />

        {loading && !data && <LoadingBlock />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}
        {data && <TotalsRow report={data} />}
      </Card>

      {data && (
        <>
          <Card>
            <CardHeader
              title={tt("cost.daily.title")}
              description={`${data.window_days} • ${data.daily.length}`}
            />
            <DailySparkline points={data.daily} />
          </Card>

          <div className="grid grid-cols-12 gap-4">
            <BreakdownCard
              className="col-span-4"
              icon={<Cpu className="size-4 text-base-500" />}
              title={tt("cost.by_provider")}
              rows={data.by_provider.map(p => ({
                label: p.provider, cost: p.cost_usd, calls: p.call_count,
              }))}
              totalCost={data.totals.cost_usd}
            />
            <BreakdownCard
              className="col-span-4"
              icon={<Activity className="size-4 text-base-500" />}
              title={tt("cost.by_agent")}
              rows={data.by_agent.map(a => ({
                label: a.agent_id, cost: a.cost_usd, calls: a.call_count,
              }))}
              totalCost={data.totals.cost_usd}
            />
            <BreakdownCard
              className="col-span-4"
              icon={<BarChart3 className="size-4 text-base-500" />}
              title={tt("cost.by_task_type")}
              rows={data.by_task_type.map(t => ({
                label: t.task_type, cost: t.cost_usd, calls: t.call_count,
              }))}
              totalCost={data.totals.cost_usd}
            />
          </div>
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Window picker
// ---------------------------------------------------------------------------


function WindowPicker(props: {
  value: number; onChange: (v: number) => void;
}): JSX.Element {
  const tt = useT();
  return (
    <div className="flex items-center gap-1 rounded-sm border border-base-200 bg-base-50 p-0.5">
      {WINDOW_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => props.onChange(opt.value)}
          className={
            "rounded-sm px-2 py-0.5 text-2xs font-medium transition-colors " +
            (props.value === opt.value
              ? "bg-primary-900 text-white"
              : "text-base-700 hover:bg-base-100")
          }
        >
          {tt(opt.labelKey)}
        </button>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Totals — 4 mini-stats in a row
// ---------------------------------------------------------------------------


function TotalsRow({ report }: { report: CostReport }): JSX.Element {
  const tt = useT();
  const t = report.totals;
  return (
    <div className="grid grid-cols-12 gap-3">
      <Stat
        icon={<Coins className="size-4 text-ai-600" />}
        label={tt("cost.tile.spend")}
        primary={`$${t.cost_usd.toFixed(2)}`}
        secondary={tt("cost.tile.spend.secondary", { calls: t.call_count.toLocaleString("ru-RU") })}
      />
      <Stat
        icon={<Zap className="size-4 text-ai-600" />}
        label={tt("cost.tile.tokens")}
        primary={formatTokens(t.total_tokens)}
        secondary={tt("cost.tile.tokens.secondary", { avg: Math.round(t.total_tokens / Math.max(t.call_count, 1)) })}
      />
      <Stat
        icon={<Activity className="size-4 text-ai-600" />}
        label={tt("cost.tile.latency")}
        primary={tt("cost.tile.latency.value", { ms: t.avg_latency_ms })}
        secondary={tt("cost.tile.latency.secondary")}
      />
      <Stat
        icon={<Cpu className="size-4 text-ai-600" />}
        label="Cache hit"
        primary={`${Math.round(t.cache_hit_ratio * 100)}%`}
        secondary={t.cache_hit_ratio > 0.4 ? tt("cost.tile.cache.good") : tt("cost.tile.cache.low")}
      />
    </div>
  );
}


function Stat(props: {
  icon: JSX.Element;
  label: string;
  primary: string;
  secondary: string;
}): JSX.Element {
  return (
    <div className="col-span-3 rounded-sm border border-base-200 bg-surface px-3 py-2">
      <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide text-base-500">
        {props.icon}
        {props.label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-base-900">
        {props.primary}
      </div>
      <div className="text-2xs text-base-500">{props.secondary}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sparkline — minimal SVG line + filled area
// ---------------------------------------------------------------------------


function DailySparkline({ points }: { points: CostDailyPoint[] }): JSX.Element {
  const tt = useT();
  if (points.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-base-500">
        {tt("cost.empty.period")}
      </div>
    );
  }
  const W = 640;
  const H = 100;
  const PAD = 8;

  const maxCost = Math.max(...points.map(p => p.cost_usd), 0.001);
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);

  const path = points
    .map((p, i) => {
      const x = PAD + i * stepX;
      const y = PAD + (1 - p.cost_usd / maxCost) * (H - PAD * 2);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  const area =
    points.length > 1
      ? `${path} L ${PAD + (points.length - 1) * stepX} ${H - PAD} L ${PAD} ${H - PAD} Z`
      : "";

  return (
    <div className="flex flex-col gap-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
        {area && <path d={area} fill="rgb(99 102 241 / 0.12)" />}
        <path d={path} stroke="rgb(99 102 241)" strokeWidth={1.5} fill="none" />
        {points.map((p, i) => {
          const x = PAD + i * stepX;
          const y = PAD + (1 - p.cost_usd / maxCost) * (H - PAD * 2);
          return (
            <circle key={p.day} cx={x} cy={y} r={2} fill="rgb(99 102 241)">
              <title>{tt("cost.chart.tip", { day: p.day, cost: p.cost_usd.toFixed(4), calls: p.call_count })}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex items-center justify-between text-2xs text-base-500 tabular-nums">
        <span>{points[0]?.day ?? ""}</span>
        <span>{tt("cost.chart.peak", { cost: maxCost.toFixed(4) })}</span>
        <span>{points[points.length - 1]?.day ?? ""}</span>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Breakdown card — horizontal bars
// ---------------------------------------------------------------------------


interface BreakdownRow { label: string; cost: number; calls: number; }


function BreakdownCard(props: {
  className?: string;
  icon: JSX.Element;
  title: string;
  rows: BreakdownRow[];
  totalCost: number;
}): JSX.Element {
  const tt = useT();
  const max = useMemo(
    () => Math.max(...props.rows.map(r => r.cost), 0.001),
    [props.rows],
  );
  return (
    <Card className={props.className}>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {props.icon}
            {props.title}
          </span>
        }
      />
      {props.rows.length === 0 ? (
        <div className="py-4 text-center text-xs text-base-500">{tt("cost.empty.short")}</div>
      ) : (
        <ul className="space-y-1.5">
          {props.rows.slice(0, 10).map((r) => (
            <li key={r.label}>
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate font-mono text-base-800">{r.label}</span>
                <span className="shrink-0 tabular-nums text-base-600">
                  ${r.cost.toFixed(4)} · {r.calls}
                </span>
              </div>
              <div className="mt-0.5 h-1.5 w-full rounded-sm bg-base-100">
                <div
                  className="h-full rounded-sm bg-ai-500"
                  style={{ width: `${(r.cost / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------


function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}
